import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ShieldCheck, Plus, Copy, Ban, KeyRound, RefreshCw, ShieldAlert, LogIn, Gauge, MailWarning, FileWarning } from "lucide-react";
import { toast } from "sonner";

const EVENT_META = {
  login_failed: { label: "Failed login", icon: LogIn, cls: "bg-amber-100 text-amber-700 border-amber-200" },
  rate_limited: { label: "Rate limit hit", icon: Gauge, cls: "bg-orange-100 text-orange-700 border-orange-200" },
  csrf_blocked: { label: "CSRF blocked", icon: ShieldAlert, cls: "bg-rose-100 text-rose-700 border-rose-200" },
  unverified_sender: { label: "Unverified sender", icon: MailWarning, cls: "bg-rose-100 text-rose-700 border-rose-200" },
  attachment_too_large: { label: "Oversized attachment", icon: FileWarning, cls: "bg-slate-100 text-slate-700 border-slate-200" },
};

export default function Admin() {
  const [key, setKey] = useState("");
  const [authed, setAuthed] = useState(false);
  const [codes, setCodes] = useState([]);
  const [form, setForm] = useState({ max_uses: 1, expires_days: "", label: "" });
  const [busy, setBusy] = useState(false);
  const [security, setSecurity] = useState(null);
  const [loadingSec, setLoadingSec] = useState(false);

  const hdr = (k) => ({ headers: { "X-Admin-Key": k } });

  const loadCodes = async (k) => {
    const { data } = await api.get("/admin/invite-codes", hdr(k));
    setCodes(data);
  };

  const loadSecurity = async (k) => {
    setLoadingSec(true);
    try {
      const { data } = await api.get("/admin/security-events", hdr(k || key));
      setSecurity(data);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setLoadingSec(false); }
  };

  const unlock = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      await loadCodes(key);
      await loadSecurity(key);
      setAuthed(true);
    } catch (err) {
      toast.error(err.response?.status === 403 ? "Invalid admin key" : apiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const createCode = async () => {
    try {
      const payload = { max_uses: Number(form.max_uses) || 1, label: form.label };
      if (form.expires_days) payload.expires_days = Number(form.expires_days);
      await api.post("/admin/invite-codes", payload, hdr(key));
      toast.success("Invite code created");
      setForm({ max_uses: 1, expires_days: "", label: "" });
      loadCodes(key);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const revoke = async (id) => {
    await api.post(`/admin/invite-codes/${id}/revoke`, {}, hdr(key));
    toast.success("Code revoked");
    loadCodes(key);
  };

  const copy = (code) => { navigator.clipboard.writeText(code); toast.success(`Copied ${code}`); };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-8">
        <Card className="w-full max-w-sm p-6 border border-border">
          <div className="flex items-center gap-2 mb-1"><ShieldCheck className="h-5 w-5 text-primary" /><h1 className="font-display font-black text-xl">Platform Admin</h1></div>
          <p className="text-sm text-muted-foreground mb-4">Enter the platform admin key to manage workspace invite codes.</p>
          <form onSubmit={unlock} className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest flex items-center gap-1"><KeyRound className="h-3.5 w-3.5" />Admin key</Label>
              <Input type="password" value={key} onChange={(e) => setKey(e.target.value)} className="mt-1.5 font-mono" data-testid="admin-key" placeholder="••••••••" />
            </div>
            <Button type="submit" className="w-full" disabled={busy} data-testid="admin-unlock">{busy ? "Checking…" : "Unlock"}</Button>
          </form>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="h-16 border-b border-border bg-card px-8 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-primary" />
          <h1 className="font-display font-black text-xl">Platform Admin</h1>
        </div>
        <Button variant="outline" size="sm" onClick={() => { setKey(""); setAuthed(false); setSecurity(null); }} data-testid="admin-lock">Lock</Button>
      </header>
      <div className="p-8 space-y-6 max-w-4xl">
        {/* Security Activity */}
        <Card className="p-6 border border-border" data-testid="security-activity-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-primary" /><h2 className="font-display font-bold text-lg">Security Activity</h2></div>
            <Button variant="outline" size="sm" onClick={() => loadSecurity()} disabled={loadingSec} data-testid="refresh-security-btn"><RefreshCw className={`h-4 w-4 mr-1 ${loadingSec ? "animate-spin" : ""}`} />Refresh</Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
            {Object.entries(EVENT_META).map(([type, m]) => {
              const Icon = m.icon;
              const c24 = security?.last_24h?.[type] || 0;
              const c7 = security?.last_7d?.[type] || 0;
              return (
                <div key={type} className="rounded-sm border border-border p-3" data-testid={`sec-stat-${type}`}>
                  <div className="flex items-center gap-1.5 text-muted-foreground"><Icon className="h-3.5 w-3.5" /><span className="text-[11px] uppercase tracking-wide">{m.label}</span></div>
                  <p className="font-display font-black text-3xl mt-1 leading-none">{c24}</p>
                  <p className="text-[11px] text-muted-foreground mt-1">24h · {c7} in 7d</p>
                </div>
              );
            })}
          </div>
          <div className="border border-border rounded-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow><TableHead>Event</TableHead><TableHead>Who</TableHead><TableHead>Detail</TableHead><TableHead>IP</TableHead><TableHead>When</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {(!security || security.events.length === 0) && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-10">{security ? "No security events recorded yet — that's a good thing." : "Loading…"}</TableCell></TableRow>}
                {security?.events?.map((ev) => {
                  const m = EVENT_META[ev.type] || { label: ev.type, cls: "bg-secondary text-foreground border-border" };
                  return (
                    <TableRow key={ev.id} data-testid={`sec-event-row`}>
                      <TableCell><Badge variant="outline" className={m.cls}>{m.label}</Badge></TableCell>
                      <TableCell className="text-sm">{ev.email || "—"}</TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[16rem] truncate">{ev.detail || "—"}</TableCell>
                      <TableCell className="text-xs font-mono">{ev.ip || "—"}</TableCell>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{new Date(ev.created_at).toLocaleString()}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <p className="text-xs text-muted-foreground mt-3">Shows the last 200 security events across all workspaces (auto-expire after 30 days). Failed logins, rate-limit hits, CSRF blocks, and blocked/oversized email uploads.</p>
        </Card>

        <Card className="p-6 border border-border">
          <h2 className="font-display font-bold text-lg mb-4">Generate workspace invite</h2>
          <div className="grid sm:grid-cols-4 gap-3 items-end">
            <div>
              <Label className="text-xs uppercase tracking-widest">Max uses</Label>
              <Input type="number" min="1" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} className="mt-1.5" data-testid="code-max-uses" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Expires (days)</Label>
              <Input type="number" min="1" value={form.expires_days} onChange={(e) => setForm({ ...form, expires_days: e.target.value })} className="mt-1.5" placeholder="never" data-testid="code-expires" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Label</Label>
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="mt-1.5" placeholder="e.g. ABC Corp" data-testid="code-label" />
            </div>
            <Button onClick={createCode} data-testid="create-code-btn"><Plus className="h-4 w-4 mr-1" />Generate</Button>
          </div>
          <p className="text-xs text-muted-foreground mt-3">Single-use = max uses 1. Multi-use = set a higher number (e.g. 5 companies can sign up with one code).</p>
        </Card>

        <Card className="border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow><TableHead>Code</TableHead><TableHead>Label</TableHead><TableHead>Uses</TableHead><TableHead>Status</TableHead><TableHead className="w-24"></TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {codes.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-10">No codes yet.</TableCell></TableRow>}
              {codes.map((c) => (
                <TableRow key={c.id} data-testid={`code-row-${c.code}`}>
                  <TableCell className="font-mono font-medium">{c.code}</TableCell>
                  <TableCell className="text-muted-foreground">{c.label || "—"}</TableCell>
                  <TableCell>{c.uses}/{c.max_uses}</TableCell>
                  <TableCell>
                    {!c.active ? <Badge variant="secondary">Revoked</Badge>
                      : c.uses >= c.max_uses ? <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-200">Used up</Badge>
                      : <Badge variant="outline" className="bg-emerald-100 text-emerald-700 border-emerald-200">Active</Badge>}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" onClick={() => copy(c.code)} data-testid={`copy-${c.code}`}><Copy className="h-4 w-4" /></Button>
                      {c.active && <Button size="icon" variant="ghost" onClick={() => revoke(c.id)} data-testid={`revoke-${c.code}`}><Ban className="h-4 w-4 text-destructive" /></Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>
    </div>
  );
}
